import { getCookieCustom } from '../utils/cookies';

type AuthCookieUser = {
	currentlyActiveDb?: string;
	databases?: string[];
};

const slugify = (value: string) =>
	value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

const getCurrentDatabaseSlug = () => {
	const userCookie = getCookieCustom('user');
	if (!userCookie) {
		return '';
	}

	try {
		const user = JSON.parse(userCookie) as AuthCookieUser;
		if (!user.currentlyActiveDb || !Array.isArray(user.databases)) {
			return '';
		}

		const activeDatabase = user.databases.find((databaseItem) =>
			databaseItem.startsWith(`${user.currentlyActiveDb}:`),
		);
		if (!activeDatabase) {
			return '';
		}

		const databaseName = activeDatabase.split(':').slice(1).join(':').trim();
		if (!databaseName) {
			return '';
		}

		return slugify(databaseName);
	} catch {
		return '';
	}
};

const storagePrefix = [import.meta.env.VITE_APP_NAME, getCurrentDatabaseSlug()]
	.filter(Boolean)
	.join('_');

const buildKey = (name: string) => `${storagePrefix}_${name}`;

const parseStored = (raw: string | null) => {
	if (!raw) return null;
	try {
		return JSON.parse(raw);
	} catch {
		return raw;
	}
};

const getConfigStorageItem = (key: string) => {
	return parseStored(localStorage.getItem(buildKey(key)));
};

const setConfigStorageItem = ({
	page,
	key,
	value,
}: {
	page?: string;
	key: string;
	value: string | object;
}) => {
	if (!page) {
		const serialized =
			typeof value === 'string' ? value : JSON.stringify(value);
		localStorage.setItem(buildKey(key), serialized);
		return;
	}

	const existing = getConfigStorageItem(page);
	const currentObject =
		existing && typeof existing === 'object' && !Array.isArray(existing)
			? (existing as Record<string, unknown>)
			: {};

	localStorage.setItem(
		buildKey(page),
		JSON.stringify({ ...currentObject, [key]: value }),
	);
};

const removeConfigStorageItem = ({
	page,
	key,
}: {
	page?: string;
	key: string;
}) => {
	if (!page) {
		localStorage.removeItem(buildKey(key));
		return;
	}

	const existing = getConfigStorageItem(page);
	if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
		return;
	}

	const next: Record<string, unknown> = {
		...(existing as Record<string, unknown>),
	};
	delete next[key];
	localStorage.setItem(buildKey(page), JSON.stringify(next));
};

const STRUCTURE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

type StructureCacheEntry = {
	lang: string;
	cachedAt: number;
	items: unknown[];
};

/**
 * Table structure cache, stored per page under the page object's `structure`
 * key and stamped with language + write time: the backend structure changes
 * on deploys and differs per language, so entries expire after 24h and never
 * serve a different language. The legacy shape (a bare structure array with
 * no stamp) is treated as a miss so it gets refetched and rewritten.
 */
const readCachedStructure = (page: string, lang: string): unknown[] | null => {
	const entry = getConfigStorageItem(page)?.structure as
		| StructureCacheEntry
		| unknown[]
		| undefined;
	if (!entry || Array.isArray(entry)) return null;
	if (
		entry.lang !== lang ||
		!Array.isArray(entry.items) ||
		entry.items.length === 0 ||
		Date.now() - entry.cachedAt > STRUCTURE_CACHE_TTL_MS
	) {
		return null;
	}
	return entry.items;
};

const writeCachedStructure = (
	page: string,
	lang: string,
	items: unknown[],
): void => {
	if (!Array.isArray(items) || items.length === 0) return;
	setConfigStorageItem({
		page,
		key: 'structure',
		value: { lang, cachedAt: Date.now(), items },
	});
};

// Drops the cached structure for a page so the next read refetches it. Used
// after a page's schema changes (e.g. dynamic-table column add/delete).
const clearCachedStructure = (page: string): void => {
	removeConfigStorageItem({ page, key: 'structure' });
};

export {
	clearCachedStructure,
	getConfigStorageItem,
	readCachedStructure,
	removeConfigStorageItem,
	setConfigStorageItem,
	storagePrefix,
	writeCachedStructure,
};
